import { useEffect, useRef } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'

import { handleException } from 'clients/exception-handling'
import { ErrorBoundaryContent } from 'components/error-boundary/ErrorBoundaryContent'
import { NotFound } from 'features/not-found'

export const RouteErrorBoundary = (): JSX.Element => {
  const error = useRouteError()
  const handled = useRef<{ cause: unknown }>()

  useEffect(() => {
    if (handled.current && Object.is(handled.current.cause, error)) return
    handled.current = { cause: error }
    const routeResponse = isRouteErrorResponse(error)
    const cause = routeResponse
      ? new Error(`Route response ${error.status}`)
      : error
    handleException(cause, {
      disposition: 'expected',
      isExpectedError: () => routeResponse && error.status === 404,
      reason: 'Missing routes render the not-found view.',
      message: 'Route failed to render.',
      operation: 'route.render',
      ...(routeResponse ? { context: { http_status: error.status } } : {}),
    })
  }, [error])

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />
  }

  return (
    <div
      data-testid="route-error-boundary"
      style={{ height: '100%', width: '100%' }}
    >
      <ErrorBoundaryContent />
    </div>
  )
}
